import { Fragment, useEffect, useId, useRef, useState } from "react";
import Svg, { Circle, Defs, LinearGradient, Path, Stop } from "react-native-svg";
import { animateOnly, Badge, Crossfade, DrawPath, FadeInPath, getTokenValue, lineGrow, useTheme, XStack, YStack } from "@medbillai/ui";
import type { ChartPoint } from "../read/readCoverage";
import { ScrubArea, type ScrubAreaProps } from "./ScrubArea";
import { ScrubTip } from "./ScrubTip";
import { loneUnknownUntil, pointAt, readingAt, stepPath } from "./stepPath";
const STROKE = 3;
/** Room above the limit line for the pill that names it. */
const PILL_HALF = getTokenValue("$sp1.5", "space");
/** The solid run that marks today after a single reading's unknown year. */
const TODAY_STEP = getTokenValue("$sp4", "space");
const MARK_RADIUS = getTokenValue("$sp0.75", "space");
const MARK_GAP = getTokenValue("$sp1", "space");
/** The reference line's small pill. */
const SMALL_PILL = getTokenValue("$si2.5", "size");
export type StepChartProps = {
  points: ChartPoint[];
  limit: number;
  start: Date;
  end: Date;
  /** The side margins the readings and the scrub stay inside. */
  inset?: number;
  height: number;
  /** What the chart says, for screen readers. */
  label: string;
  /** Names the limit line, e.g. "Deductible". */
  limitLabel: string;
  limitLeft?: "$sp1.5" | "$sp2" | 0;
  limitTop?: number;
  strokeWidth?: number;
  /** A point the line passed, e.g. where the deductible was met. */
  marker?: {
    at: Date;
    label: string;
  } | null;
  /** A level below the limit with its own line, e.g. a met deductible. */
  reference?: {
    amount: number;
    label: string;
  } | null;
  /** Press and drag to read each reading; off where the chart only glances. */
  scrub?: boolean;
  /** On the first scrub, and again after `scrubKey` changes. */
  onScrubStart?: () => void;
  /** The chart being shown, when one mount shows several in turn. */
  scrubKey?: string;
  /** Set to draw the line in and fade the fill up; a new value draws again. */
  drawInKey?: string;
};

/** A figure's readings as a step line climbing toward its limit. */
export function StepChart({
  points,
  limit,
  start,
  end,
  inset = 0,
  height,
  label,
  limitLabel,
  limitLeft = 0,
  limitTop = PILL_HALF,
  strokeWidth = STROKE,
  marker = null,
  reference = null,
  scrub = true,
  onScrubStart,
  scrubKey = "",
  drawInKey
}: StepChartProps) {
  const theme = useTheme();
  const [width, setWidth] = useState(0);
  const [scrubX, setScrubX] = useState<number | null>(null);
  const reportedFor = useRef<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: resets per chart shown
  useEffect(() => {
    reportedFor.current = null;
  }, [scrubKey]);
  const onScrub = (x: number | null) => {
    if (x !== null && reportedFor.current !== scrubKey) {
      reportedFor.current = scrubKey;
      onScrubStart?.();
    }
    setScrubX(x);
  };
  // useId's punctuation (`:r0:`, `«r0»`) breaks `url(#…)` on web.
  const fillId = `step${useId().replace(/[^\w-]/g, "")}`;
  const box = {
    width,
    height,
    top: limitTop,
    limit,
    start,
    end,
    inset,
    reference: reference?.amount,
    minReferenceGap: PILL_HALF + SMALL_PILL / 2
  };
  const paths = width > 0 ? stepPath(points, box) : null;
  const scrubbed = paths && scrubX !== null ? readingAt(points, scrubX, box) : null;
  const unknownUntil = paths?.lone ? loneUnknownUntil(width, inset, TODAY_STEP) : null;
  const mark = paths && marker ? pointAt(points, marker.at, box) : null;
  const Area = scrub ? ScrubArea : StillArea;
  const line = theme.borderBrand.val;
  const fill = theme.bgBrandIntense.val;
  const fillUrl = `url(#${fillId})`;
  return <YStack height={height} accessible role="img" aria-label={label} onLayout={e => setWidth(e.nativeEvent.layout.width)} data-sentry-element="YStack" data-sentry-component="StepChart" data-sentry-source-file="StepChart.tsx">
      <Area onScrub={onScrub} data-sentry-element="Area" data-sentry-source-file="StepChart.tsx">
        {paths ? <Svg width={width} height={height}>
            <Defs>
              <LinearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
                <Stop offset="0" stopColor={fill} stopOpacity={0.46} />
                <Stop offset="0.62" stopColor={fill} stopOpacity={0.1} />
                <Stop offset="1" stopColor={fill} stopOpacity={0} />
              </LinearGradient>
            </Defs>
            <Path d={`M0 ${height - 0.5} H${width}`} stroke={theme.borderInverse.val} strokeOpacity={0.15} strokeWidth={1} />
            {paths.lone ? <TodayStep width={width} stepX={unknownUntil ?? 0} height={height} y={paths.levelY} color={line} unknown={theme.borderInverse.val} fillUrl={fillUrl} strokeWidth={strokeWidth} drawInKey={drawInKey} /> : <Drawn fill={paths.fill} fillUrl={fillUrl} line={paths.line} strokeLength={paths.strokeLength} stroke={line} strokeWidth={strokeWidth} drawInKey={drawInKey} />}
            {mark ? <Circle cx={mark.x} cy={mark.y} r={MARK_RADIUS} fill={theme.surfaceInverse.val} stroke={line} strokeWidth={strokeWidth} /> : null}
          </Svg> : null}
        {paths ? <XStack position="absolute" left={limitLeft} right={0} top={paths.thresholdY - PILL_HALF} height="$si3" alignItems="center" pointerEvents="none">
            <LimitPill label={limitLabel} />
            <YStack key={drawInKey} flex={1} height="$si0.25" backgroundColor="$borderBrand" {...drawInKey === undefined ? {} : lineGrow} />
          </XStack> : null}
        {paths && reference && paths.referenceY !== null ? <XStack position="absolute" left={limitLeft} right={0} top={paths.referenceY - SMALL_PILL / 2} height={SMALL_PILL} alignItems="center" pointerEvents="none">
            <Badge size="small" status="brand" bordered={false}>
              {reference.label}
            </Badge>
            <YStack flex={1} height="$si0.25" backgroundColor="$borderBrand" />
          </XStack> : null}
        {mark && marker && paths?.referenceY === null ? <XStack position="absolute" bottom={height - mark.y + MARK_GAP}
      // The label leans away from the nearer edge.
      {...mark.x > width / 2 ? {
        right: width - mark.x - MARK_GAP
      } : {
        left: mark.x - MARK_GAP
      }} pointerEvents="none">
            <Badge size="small" status="brand" bordered={false}>
              {marker.label}
            </Badge>
          </XStack> : null}
        {scrubbed ? <ScrubTip width={width} inset={inset} reading={scrubbed} beforeFirstCheck={scrubbed.x < (unknownUntil ?? 0)} /> : null}
      </Area>
    </YStack>;
}
function StillArea({
  children
}: ScrubAreaProps) {
  return <YStack height="100%" data-sentry-element="YStack" data-sentry-component="StillArea" data-sentry-source-file="StepChart.tsx">{children}</YStack>;
}

/**
 * A single reading: the year before it tinted from $0 up to today's level,
 * since totals only rise and the year so far sits somewhere inside, then a
 * short solid run up to today.
 */
function TodayStep({
  width,
  stepX,
  height,
  y,
  color,
  unknown,
  fillUrl,
  strokeWidth,
  drawInKey
}: {
  width: number;
  stepX: number;
  height: number;
  y: number;
  color: string;
  unknown: string;
  fillUrl: string;
  strokeWidth: number;
  drawInKey?: string;
}) {
  return <>
      <Path d={`M0 ${y} H${stepX} V${height} H0 Z`} fill={unknown} fillOpacity={0.08} data-sentry-element="Path" data-sentry-source-file="StepChart.tsx" />
      <Path d={`M0 ${y} H${stepX}`} stroke={unknown} strokeOpacity={0.3} strokeWidth={1} data-sentry-element="Path" data-sentry-source-file="StepChart.tsx" />
      <Drawn fill={`M${stepX} ${y} H${width} V${height} H${stepX} Z`} fillUrl={fillUrl} line={`M${stepX} ${y} H${width}`} strokeLength={width - stepX} stroke={color} strokeWidth={strokeWidth} drawInKey={drawInKey} data-sentry-element="Drawn" data-sentry-source-file="StepChart.tsx" />
    </>;
}

/** The line over its fill, drawn in when `drawInKey` is set. */
function Drawn({
  fill,
  fillUrl,
  line,
  strokeLength,
  stroke,
  strokeWidth,
  drawInKey
}: {
  fill: string;
  fillUrl: string;
  line: string;
  strokeLength: number;
  stroke: string;
  strokeWidth: number;
  drawInKey?: string;
}) {
  const lineProps = {
    d: line,
    stroke,
    strokeWidth
  };
  return drawInKey === undefined ? <>
      <Path d={fill} fill={fillUrl} data-sentry-element="Path" data-sentry-source-file="StepChart.tsx" />
      <Path {...lineProps} fill="none" strokeLinejoin="round" strokeLinecap="round" data-sentry-element="Path" data-sentry-source-file="StepChart.tsx" />
    </> : <Fragment key={drawInKey}>
      <FadeInPath d={fill} fill={fillUrl} data-sentry-element="FadeInPath" data-sentry-source-file="StepChart.tsx" />
      <DrawPath {...lineProps} length={strokeLength} data-sentry-element="DrawPath" data-sentry-source-file="StepChart.tsx" />
    </Fragment>;
}
function LimitPill({
  label
}: {
  label: string;
}) {
  const [width, setWidth] = useState(0);
  return <>
      <Badge status="brand" bordered={false} position="absolute" opacity={0} pointerEvents="none" aria-hidden onLayout={e => setWidth(e.nativeEvent.layout.width)} data-sentry-element="Badge" data-sentry-source-file="StepChart.tsx">
        {label}
      </Badge>
      <YStack width={width || undefined} borderRadius="$brFull" backgroundColor="$bgBrandIntense" overflow="hidden" transition="expand" animateOnly={animateOnly("width")} data-sentry-element="YStack" data-sentry-source-file="StepChart.tsx">
        <Crossfade stateKey={label} data-sentry-element="Crossfade" data-sentry-source-file="StepChart.tsx">
          <Badge status="brand" bordered={false} backgroundColor="transparent" alignSelf="flex-start" data-sentry-element="Badge" data-sentry-source-file="StepChart.tsx">
            {label}
          </Badge>
        </Crossfade>
      </YStack>
    </>;
}